/**
 * What a reading costs, in carats.
 *
 * The price lives on the active ReaderSkillVariant and reaches the FE with the
 * reader payload. It was read in one place (ReceptionConfirm) and hardcoded in
 * two others, which is how the astrology paywall came to say "25" and gate on
 * `balance < 25` while the actual charge was 100 carats — a seeker with 50
 * carats passed the gate and then failed at the till.
 *
 * The fallback is the reading FLOOR, not a made-up number: if no variant has
 * loaded yet, the least a reading can cost is the floor.
 */

import { formatAmount } from '@/utils/carats'

/** Reading floor in carats. Mirrors pricing.DEFAULTS['reading_floor']. */
export const READING_FLOOR_CARATS = 50

export function readingPriceCarats(variants, fallback = READING_FLOOR_CARATS) {
  const list = Array.isArray(variants) ? variants : []
  const active = list.find(v => v && v.is_active)
  const price = active && Number(active.price_carats)
  return Number.isFinite(price) && price >= 0 ? price : fallback
}

/** Can this seeker afford a reading right now? Subscribers always can. */
export function canAffordReading({ balance = 0, variants = [], subscribed = false } = {}) {
  if (subscribed) return true
  return (Number(balance) || 0) >= readingPriceCarats(variants)
}

// ── The price the seeker is actually shown ──────────────────────────────
//
// `readingPriceCarats(store.readerSkillVariants)` looks like the answer and is
// not one on the seeker path: that array is written ONLY by Studio code, so on
// a reader page it is always empty and the helper always returned the 50-carat
// floor while the Doorman charged the variant's 100. The price now travels with
// the reader payload as `reading_price: { carats, pay_at_reception }`.
//
// These read null — never a number — when nothing has loaded, so a surface that
// does not yet know the price renders nothing instead of a wrong figure.

/** Carats this reader charges for a reading, or null if we weren't told. */
export function readerReadingPrice(readerDetail) {
  const carats = readerDetail && readerDetail.reading_price
    && readerDetail.reading_price.carats
  const n = Number(carats)
  if (carats === null || carats === undefined || carats === '') return null
  return Number.isFinite(n) && n >= 0 ? n : null
}

/** Does the seeker pay before the reading begins, rather than at a paywall? */
export function readerChargesUpFront(readerDetail) {
  const price = readerReadingPrice(readerDetail)
  const payAtReception = !!(readerDetail && readerDetail.reading_price
    && readerDetail.reading_price.pay_at_reception)
  return payAtReception && price !== null && price > 0
}

/** Compact price for a button: '' when unknown or zero (never the word "Free"). */
export function readingPriceBadge(carats) {
  if (carats === null || carats === undefined) return ''
  const n = Number(carats)
  if (!Number.isFinite(n) || n < 0) return ''
  if (n === 0) return ''
  return formatAmount(n)
}

/**
 * The same price as a sentence, for a line beside the button.
 *
 * `upFront` reflects the reader's pay_at_reception: the seeker either pays as
 * the reading begins, or at the reveal partway through. Both are worth saying —
 * "what does it cost" and "when do I pay" are different questions.
 */
export function readingCostSentence(carats, upFront = false) {
  if (carats === null || carats === undefined) return ''
  const n = Number(carats)
  if (!Number.isFinite(n) || n < 0) return ''
  if (n === 0) return 'This reading is free.'
  if (upFront) return `You pay ${formatAmount(n)} when your reading begins.`
  return `This reading costs ${formatAmount(n)}.`
}
